import { plural } from '../format';

/**
 * The setup area's words (console redesign, docs/console-redesign-plan.md).
 *
 * One file per area so the redesign's parallel work never edits the same
 * dictionary. `en.ts` and `es.ts` spread these in; the Spanish half is typed
 * against the English one, so a key missing here is a build failure exactly as
 * it is in the main catalog.
 *
 * The Setup tab's rule for words is the plan's principle 2: a feature is named
 * by what it does for the business ("Checkout", "Gift cards"), never by its
 * module slug; a dependency is a sentence ("needs Checkout"); the booking rule
 * is read as a sentence with its numbers highlighted (`**…**`, which `Rich`
 * turns into the highlight). The counter questions themselves are still the
 * `settings.*` entries in the main catalog — they were already plain.
 */
export const setupEn = {
  'setup.dialog.close': 'Close',

  /* ---- Features --------------------------------------------------- */
  'setup.features.title': 'Features',
  'setup.features.lede': ({ name }: { name: string }) => `What ${name} has in its app`,
  'setup.features.group.day': 'Running the day',
  'setup.features.group.selling': 'Selling',
  'setup.features.group.reaching': 'Reaching clients',
  'setup.features.group.kinds': 'Other kinds of business',
  'setup.features.group.behind': 'Behind the scenes',
  'setup.features.alwaysOn': 'Always on',
  'setup.features.trial': 'Trial',
  'setup.features.needs': ({ list }: { list: string }) => `needs ${list}`,
  'setup.features.since': ({ date }: { date: string }) => `on since ${date}`,
  'setup.features.folded': ({ count }: { count: number }) =>
    `${String(count)} more, for other kinds of business`,
  'setup.features.show': 'Show',
  'setup.features.hide': 'Hide',
  'setup.features.more': ({ name }: { name: string }) => `More for ${name}`,
  'setup.features.menu.trial': 'Turn on as a trial',
  'setup.features.menu.trialHint': 'Works like on, and is marked as a trial so it is easy to end.',
  'setup.features.menu.toTrial': 'Mark as a trial',
  'setup.features.menu.toTrialHint': 'Stays on, marked as a trial.',
  'setup.features.menu.keep': 'Keep it — end the trial',
  'setup.features.menu.keepHint': 'Stays on, no longer marked as a trial.',
  'setup.features.menu.off': 'Turn off…',
  'setup.features.menu.offHint': 'Asks first, and says what goes with it.',
  'setup.features.on.title': ({ name }: { name: string }) => `Turn on ${name}?`,
  'setup.features.on.consequence': ({
    name,
    needs,
    business,
  }: {
    name: string;
    needs: string;
    business: string;
  }) =>
    `${name} needs **${needs}**, which ${business} does not have yet. They are turned on together.`,
  'setup.features.on.trialConsequence': ({
    name,
    needs,
    business,
  }: {
    name: string;
    needs: string;
    business: string;
  }) =>
    `${name} needs **${needs}**, which ${business} does not have yet. Those are turned on, and ${name} starts as a trial.`,
  'setup.features.on.confirm': ({ count }: { count: number }) =>
    count === 2 ? 'Turn on both' : `Turn on all ${String(count)}`,
  'setup.features.off.title': ({ name }: { name: string }) => `Turn off ${name}?`,
  'setup.features.off.gone': ({ gone, business }: { gone: string; business: string }) =>
    `${gone} disappear from ${business}’s app.`,
  'setup.features.off.alsoGoes': ({ list }: { list: string }) =>
    `**${list}** need it, so ${plural(list.split(', ').length, 'it goes', 'they go')} off too.`,
  'setup.features.off.keeps':
    'Nothing is deleted: turning it back on brings everything back as it was.',
  'setup.features.off.confirm': ({ count }: { count: number }) =>
    count === 1 ? 'Turn off' : count === 2 ? 'Turn off both' : `Turn off all ${String(count)}`,
  'setup.features.done.on': ({ list }: { list: string }) => `Turned on: ${list}.`,
  'setup.features.done.trial': ({ list }: { list: string }) => `On as a trial: ${list}.`,
  'setup.features.done.kept': ({ list }: { list: string }) =>
    `${list}: the trial is over, and it stays on.`,
  'setup.features.done.off': ({ list }: { list: string }) =>
    `Turned off: ${list}. Nothing was deleted.`,

  /*
   * One entry per feature: its name, what it does, and what disappears when
   * it goes off. Keyed by the module slug in `features.ts`, never shown.
   */
  'setup.feature.foundation.name': 'Calendar, clients and services',
  'setup.feature.foundation.what': 'The core of every business',
  'setup.feature.foundation.gone':
    'The calendar, the client book and the service menu — and every feature built on them —',
  'setup.feature.sales.what': 'Ring up visits and sales, and record how they paid',
  'setup.feature.sales.gone': 'The checkout and the day’s takings',
  'setup.feature.products.what': 'Sell products at the counter and online',
  'setup.feature.products.gone': 'The products and the shop',
  'setup.feature.certificates.what': 'Sell and redeem gift cards',
  'setup.feature.certificates.gone': 'Selling and redeeming gift cards',
  'setup.feature.credit.what': 'Clients keep a balance with the business',
  'setup.feature.credit.gone': 'Client balances (the balances themselves are kept)',
  'setup.feature.reporting.what': 'Money in, what sells, best clients',
  'setup.feature.reporting.gone': 'The reports',
  'setup.feature.billing.what': 'Take money online through PayPal — deposits and prepaid orders',
  'setup.feature.billing.gone': 'Taking money online',
  'setup.feature.booking_site.what': 'Their own booking site',
  'setup.feature.booking_site.gone': 'The booking website',
  'setup.feature.notifications.what': 'Reminders and confirmations to clients',
  'setup.feature.notifications.gone': 'Reminders and confirmations',
  'setup.feature.partner_api.what': 'Lets the booking assistant and partners check times and book',
  'setup.feature.partner_api.gone': 'Booking through the assistant and partners',
  'setup.feature.ordering.what': 'Takeaway orders and the kitchen screen',
  'setup.feature.ordering.gone': 'Ordering and the kitchen screen',
  'setup.feature.lodging.what': 'Rooms, nightly rates and stays',
  'setup.feature.lodging.gone': 'Rooms and stays',
  'setup.feature.departures.what': 'Scheduled departures with seats to sell',
  'setup.feature.departures.gone': 'Tours and sailings',
  'setup.feature.photos.what': 'Albums guests open with a code on the website',
  'setup.feature.photos.gone': 'Tour photo albums',
  'setup.feature.fulfillment.what': 'Things made to order and posted by a print partner',
  'setup.feature.fulfillment.gone': 'Merch and prints',
  'setup.feature.admin.what': 'Our own access to the business — never sold',
  'setup.feature.admin.gone': 'Operator access',

  /* ---- Settings, by where they show up ---------------------------- */
  'setup.settings.counter': 'At the counter',
  'setup.settings.shop': 'Shop and pickup',
  'setup.settings.kitchen': 'Kitchen and orders',
  'setup.settings.website': 'Website booking',
  'setup.settings.tenders': 'Payments they take',
  'setup.settings.tendersHint': 'The buttons at checkout. Any of them is still recorded if used.',
  'setup.settings.tips': 'Tips',
  'setup.settings.tipsHint': 'Adds a tip field at checkout.',
  /* Who may change what they sell (B10) */
  'setup.settings.editors': 'Who may change what they sell',
  'setup.settings.editorsHint':
    'Products, services and their photos, and what shows on their website — from the staff app. Read-only logins never can.',
  'setup.settings.editors.owner': 'Only the owner',
  'setup.settings.editors.ownerAndStaff': 'The owner and staff',
  'setup.settings.on': 'On',
  'setup.settings.off': 'Off',
  'setup.settings.save': 'Save',
  'setup.settings.undo': 'Undo changes',
  'setup.settings.forFeature': ({ feature }: { feature: string }) =>
    `For when ${feature} is on — it does nothing until then.`,
  'setup.settings.none':
    'Nothing to set here yet: these questions come with Checkout, Boutique, Restaurant ordering and Website.',
  'setup.settings.showAll': 'Show the settings for features that are off',
  'setup.settings.hideAll': 'Hide the settings for features that are off',
  'setup.settings.confirm.title': ({ section }: { section: string }) => `Save “${section}”?`,
  'setup.settings.confirm.lead': ({ business }: { business: string }) =>
    `This changes what ${business}’s staff and clients see:`,
  'setup.settings.confirm.save': 'Save',
  'setup.settings.change': ({ question, answer }: { question: string; answer: string }) =>
    `**${question}** ${answer}.`,
  'setup.settings.cleared': 'no default',
  'setup.settings.notSet': ({ list }: { list: string }) =>
    `Never answered, so what is shown is what happens anyway: ${list}.`,
  'setup.settings.quoted': ({ question }: { question: string }) => `“${question}”`,
  'setup.tender.cash': 'Cash',
  'setup.tender.check': 'Check',
  'setup.tender.card': 'Card',
  'setup.tender.other': 'Other',

  /* ---- How the day is booked --------------------------------------- */
  'setup.rule.title': 'How the day is booked',
  'setup.rule.try': 'Try a day…',
  'setup.rule.change': 'Change…',
  'setup.rule.tryNote':
    '“Try a day” opens the simulator: type a real day from their book and compare what each rule would offer.',
  'setup.rule.minutes': ({ n }: { n: number }) => `${String(n)} ${plural(n, 'minute', 'minutes')}`,
  'setup.rule.hours': ({ n }: { n: number }) => `${String(n)} ${plural(n, 'hour', 'hours')}`,
  'setup.rule.days': ({ n }: { n: number }) => `${String(n)} ${plural(n, 'day', 'days')}`,
  'setup.rule.hoursMinutes': ({ h, m }: { h: number; m: number }) =>
    `${String(h)} h ${String(m)} min`,
  'setup.rule.step': ({ n }: { n: number }) => `${String(n)}-minute steps`,
  'setup.rule.mode.open': 'offers any open time',
  'setup.rule.mode.no_slivers': 'never leaves a gap too short to sell',
  'setup.rule.mode.touching': 'offers only times next to something already booked',
  'setup.rule.sentence': ({
    name,
    step,
    gap,
    mode,
  }: {
    name: string;
    step: string;
    gap: string;
    mode: string;
  }) => `${name} works in **${step}**, leaves **${gap}** between clients, and **${mode}**.`,
  'setup.rule.sentenceNoGap': ({
    name,
    step,
    mode,
  }: {
    name: string;
    step: string;
    mode: string;
  }) => `${name} works in **${step}**, leaves no time between clients, and **${mode}**.`,
  'setup.rule.before': ({ gap }: { gap: string }) =>
    `Before each client, **${gap}** is kept free as well.`,
  'setup.rule.noNotice': 'No minimum notice.',
  'setup.rule.notice': ({ notice }: { notice: string }) =>
    `Clients book at least **${notice}** ahead.`,
  'setup.rule.cutoff': ({ time }: { time: string }) =>
    `After **${time}**, the same day is no longer offered.`,
  'setup.rule.keptBack': ({ count }: { count: number }) =>
    `**${String(count)} ${plural(count, 'stretch', 'stretches')} of hours** ${plural(count, 'is', 'are')} kept back from online booking.`,
  'setup.rule.packing': 'The times that fill the day best are offered first.',
  'setup.rule.fineTuned': 'There is more fine-tuning; “Try a day…” shows all of it.',
  'setup.rule.editTitle': 'Change how the day is booked',
  'setup.rule.modeLegend': 'Which times are offered',
  'setup.rule.gapAfter': 'Time between clients (minutes)',
  'setup.rule.gapBefore': 'Time kept free before each client (minutes)',
  'setup.rule.noticeLabel': 'Minimum notice (minutes)',
  'setup.rule.noticeHint': 'How far ahead a client must book. 0 is no minimum; 1440 is a day.',
  'setup.rule.cutoffLabel': 'Same-day cut-off',
  'setup.rule.cutoffHint': 'After this time, the same day is no longer offered. Empty for none.',
  'setup.rule.stepNote':
    'The step is changed from the command line; the finer rules are in “Try a day…”.',
  'setup.rule.apply': 'Apply…',
  'setup.rule.confirm.title': 'Apply this booking rule?',
  'setup.rule.confirm.consequence': ({ sentence }: { sentence: string }) =>
    `From now on: ${sentence} What clients and partners are offered changes at once. Staff booking by hand are never limited by this.`,
  'setup.rule.confirm.apply': 'Apply',
  'setup.rule.applied': 'Saved — this is what clients and partners are offered from now on.',
  'setup.sim.showConfig': 'Show the settings as text',

  /* ---- Business details -------------------------------------------- */
  'setup.details.timezone': 'Time zone',
  'setup.details.currency': 'Currency',
  'setup.details.stage': 'Stage',
  'setup.details.exact': 'Times are exact — the book here is the real one.',
  'setup.details.approximate':
    'Times are approximate — the book here is not the real one yet, so partners check before promising.',
  'setup.details.catalog': 'Menu version',
  'setup.details.catalogNote': ({ n }: { n: number }) =>
    `${String(n)} — goes up each time the menu is saved`,
  'setup.details.noCatalog': 'No menu yet',

  /* ---- Danger zone -------------------------------------------------- */
  'setup.close.title': 'Close this business',
  'setup.close.lede': ({ name }: { name: string }) =>
    `Offboarding stops all booking with ${name} — in the app, online and through partners — and starts the export and hand-over: their whole history is exported, checked, and handed to the owner. Nothing is deleted by offboarding.`,
  'setup.close.protected': ({ name }: { name: string }) =>
    `${name} is protected, so nothing can be deleted until protection is removed from the command line.`,
  'setup.close.unprotected':
    'Their data can be deleted by hand 90 days after offboarding, once the hand-over is done.',
  'setup.close.button': 'Offboard…',
  'setup.close.already': ({ name }: { name: string }) =>
    `${name} is offboarded: nothing can be booked. To bring them back, use “Change stage” at the top of the page.`,
  'setup.close.handover':
    'Export their data and hand it to the owner in person or on something they control — never by email. The offboarding checklist walks through it.',
  'setup.close.type': ({ id }: { id: string }) => `Type ${id} to confirm`,

  /* ---- Their app's look (B8) ---------------------------------------- */
  'look.title': 'Their app’s look',
  'look.chip.own': 'Their own look',
  'look.chip.standard': 'Standard look',
  'look.lede': ({ name }: { name: string }) =>
    `How ${name}’s staff app looks on their phones and tablets: two colours, the headings, the name at the top, the add button and the photograph behind it all.`,
  'look.standard': ({ name }: { name: string }) =>
    `${name}’s app looks as it ships: the standard colours, no photograph, the name in plain type and labelled add buttons.`,
  'look.change': 'Change the look…',
  'look.clear': 'Clear',
  'look.editTitle': ({ name }: { name: string }) => `${name}’s app — its look`,
  'look.draftNote': 'Nothing changes on their devices until you save.',

  'look.accent.label': 'Accent colour',
  'look.accent.hint':
    'Buttons, links and the name at the top. The soft wash beside it is worked out from this colour and used behind highlighted rows.',
  'look.ground.label': 'Ground colour',
  'look.ground.hint': 'The colour under the photograph, and the whole page when there is none.',
  'look.wash': 'Soft wash',
  'look.colour.picker': ({ label }: { label: string }) => `${label} picker`,
  'look.colour.hex': ({ label }: { label: string }) => `${label} as a hex code`,
  'look.colour.standard': 'Use the app’s own',
  'look.colour.isStandard': 'The app’s own colour',
  'look.colour.invalid': 'A hex code like #c19a3f.',

  'look.font.label': 'Headings',
  'look.font.system': 'The app’s own type',
  'look.font.serif': 'Serif (Georgia)',
  'look.font.hint':
    'Headings and the name at the top only. Lists stay in the app’s own type so they read at arm’s length.',

  'look.mark.label': 'The name at the top',
  'look.mark.plain': 'Plain — the name as written',
  'look.mark.hearts': 'Hearts — first word large, second between hearts, the rest below',
  'look.mark.hint':
    'Built from the business’s own name; there is nowhere to type a different one. A name of fewer than three words shows plain.',
  'look.mark.short': ({ name, words }: { name: string; words: number }) =>
    `“${name}” has ${String(words)} ${plural(words, 'word', 'words')}, so their app will show it plain: the hearts style needs three words or more.`,

  'look.button.label': 'The add button',
  'look.button.plus': 'Plus — labelled buttons, as the app ships',
  'look.button.heart': 'Heart — a gold heart on the add buttons',
  'look.button.hint':
    'Walk-in, new client, issue a gift card. The app’s own sentences follow the choice: with the heart, staff are told to press “the heart button”.',

  'look.photos.legend': 'Background photographs',
  'look.photos.hint':
    'One for the device held upright and one for sideways. With only one, it is used both ways. It sits softly blurred behind the lists.',
  'look.photo.portrait': 'Upright',
  'look.photo.landscape': 'Sideways',
  'look.photo.none': 'None — the ground colour shows.',
  'look.photo.of': ({ slot }: { slot: string }) => `The ${slot.toLowerCase()} photograph`,
  'look.photo.upload': ({ slot }: { slot: string }) =>
    `Upload the ${slot.toLowerCase()} photograph from this computer`,
  'look.photo.remove': ({ slot }: { slot: string }) =>
    `Remove the ${slot.toLowerCase()} photograph`,
  'look.photo.removeShort': 'Remove',
  'look.photo.paste': ({ slot }: { slot: string }) =>
    `Address of the ${slot.toLowerCase()} photograph`,
  'look.photo.pasteHint': 'Or paste the https address of a picture that is already public.',
  'look.photo.use': 'Use',
  'look.photo.badAddress': 'That is not an https address.',
  'look.photo.uploading': 'Uploading…',
  'look.upload.hint':
    'JPEG, PNG or WebP, up to 10 MB. It is stored straight away but only used once you save the look.',
  'look.upload.off':
    'This deployment cannot take a picture from the console yet. Paste the address of one that is already public instead.',
  'look.upload.heic': ({ file }: { file: string }) =>
    `${file} is HEIC, which needs converting first — half the browsers in the world will not show it. Convert it to JPEG and choose the JPEG.`,
  'look.upload.notPicture': ({ file }: { file: string }) =>
    `${file} is not a JPEG, PNG or WebP picture.`,
  'look.upload.tooBig': ({ file }: { file: string }) =>
    `${file} is larger than 10 MB. Export a smaller copy — a phone screen does not need more.`,

  'look.preview.title': 'Preview',
  'look.preview.hold': 'Hold the device',
  'look.preview.label': 'Sketch of their app',
  'look.preview.online': 'Online',
  'look.preview.today': 'Today',
  'look.preview.sample': 'Sample client',
  'look.preview.empty': 'Nothing else on the book today.',
  'look.preview.heartHint': ' Start one with the heart button.',
  'look.preview.walkIn': 'Walk-in',
  'look.preview.newClient': 'New client',
  'look.preview.note':
    'A sketch with invented rows. The colours, headings, name, button and photograph are the real choices.',

  'look.summary.accent': 'Accent',
  'look.summary.ground': 'Ground',
  'look.summary.headings': 'Headings',
  'look.summary.mark': 'Name at the top',
  'look.summary.button': 'Add button',
  'look.summary.photos': 'Photographs',
  'look.summary.photosBoth': 'Upright and sideways',
  'look.summary.photosPortrait': 'Upright only, used both ways',
  'look.summary.photosLandscape': 'Sideways only, used both ways',
  'look.summary.photosNone': 'None',
  'look.summary.standardValue': 'Standard',
  'look.summary.hearts': 'Hearts',
  'look.summary.heartsShort': 'Hearts (shows plain: the name is under three words)',
  'look.summary.plain': 'Plain',
  'look.summary.serif': 'Serif',
  'look.summary.heart': 'Heart',
  'look.summary.plus': 'Plus',

  'look.save': 'Save the look',
  'look.confirm.title': ({ name }: { name: string }) => `Change how ${name}’s app looks?`,
  'look.confirm.consequence': ({ name }: { name: string }) =>
    `Every phone and tablet running ${name}’s app shows the new look the next time the app opens or reconnects. The website is not changed. There are no saved versions of the look — to go back, set it again or clear it.`,
  'look.clearConfirm.title': ({ name }: { name: string }) =>
    `Put ${name}’s app back to its standard look?`,
  'look.clearConfirm.consequence': ({ name }: { name: string }) =>
    `The colours, headings, name style, heart button and both photographs go back to how the app ships, on every device running ${name}’s app the next time it opens or reconnects. No copy of the current look is kept.`,
  'look.clearConfirm.confirm': 'Clear the look',
  'look.saved': 'Look saved. Each device shows it the next time the app opens or reconnects.',
  'look.cleared': 'Look cleared. Their app is back to its standard look.',
  'look.movedFromEditor':
    'This editor is the website only. The staff app’s look — its colours, wordmark, heart button and background photographs — is on the Setup tab, under “Their app’s look”.',
};

export const setupEs: typeof setupEn = {
  'setup.dialog.close': 'Cerrar',

  'setup.features.title': 'Funciones',
  'setup.features.lede': ({ name }) => `Lo que ${name} tiene en su aplicación`,
  'setup.features.group.day': 'El día a día',
  'setup.features.group.selling': 'Vender',
  'setup.features.group.reaching': 'Llegar a los clientes',
  'setup.features.group.kinds': 'Otros tipos de negocio',
  'setup.features.group.behind': 'Entre bastidores',
  'setup.features.alwaysOn': 'Siempre activo',
  'setup.features.trial': 'Prueba',
  'setup.features.needs': ({ list }) => `necesita ${list}`,
  'setup.features.since': ({ date }) => `activo desde ${date}`,
  'setup.features.folded': ({ count }) => `${String(count)} más, para otros tipos de negocio`,
  'setup.features.show': 'Mostrar',
  'setup.features.hide': 'Ocultar',
  'setup.features.more': ({ name }) => `Más opciones de ${name}`,
  'setup.features.menu.trial': 'Activar como prueba',
  'setup.features.menu.trialHint':
    'Funciona como activado y queda marcado como prueba para terminarla fácilmente.',
  'setup.features.menu.toTrial': 'Marcar como prueba',
  'setup.features.menu.toTrialHint': 'Sigue activo, marcado como prueba.',
  'setup.features.menu.keep': 'Quedárselo — terminar la prueba',
  'setup.features.menu.keepHint': 'Sigue activo y deja de estar marcado como prueba.',
  'setup.features.menu.off': 'Desactivar…',
  'setup.features.menu.offHint': 'Pregunta antes y dice qué se va con ello.',
  'setup.features.on.title': ({ name }) => `¿Activar ${name}?`,
  'setup.features.on.consequence': ({ name, needs, business }) =>
    `${name} necesita **${needs}**, que ${business} todavía no tiene. Se activan juntos.`,
  'setup.features.on.trialConsequence': ({ name, needs, business }) =>
    `${name} necesita **${needs}**, que ${business} todavía no tiene. Eso se activa, y ${name} empieza como prueba.`,
  'setup.features.on.confirm': ({ count }) =>
    count === 2 ? 'Activar los dos' : `Activar los ${String(count)}`,
  'setup.features.off.title': ({ name }) => `¿Desactivar ${name}?`,
  'setup.features.off.gone': ({ gone, business }) =>
    `${gone}: desaparece de la aplicación de ${business}.`,
  'setup.features.off.alsoGoes': ({ list }) =>
    `**${list}** ${plural(list.split(', ').length, 'lo necesita, así que también se desactiva', 'lo necesitan, así que también se desactivan')}.`,
  'setup.features.off.keeps':
    'No se borra nada: al volver a activarlo, todo vuelve tal como estaba.',
  'setup.features.off.confirm': ({ count }) =>
    count === 1
      ? 'Desactivar'
      : count === 2
        ? 'Desactivar los dos'
        : `Desactivar los ${String(count)}`,
  'setup.features.done.on': ({ list }) => `Activado: ${list}.`,
  'setup.features.done.trial': ({ list }) => `Activado como prueba: ${list}.`,
  'setup.features.done.kept': ({ list }) => `${list}: la prueba terminó y sigue activo.`,
  'setup.features.done.off': ({ list }) => `Desactivado: ${list}. No se borró nada.`,

  'setup.feature.foundation.name': 'Agenda, clientes y servicios',
  'setup.feature.foundation.what': 'La base de todo negocio',
  'setup.feature.foundation.gone':
    'La agenda, la libreta de clientes y el menú de servicios — y todas las funciones que dependen de ellos',
  'setup.feature.sales.what': 'Cobrar visitas y ventas, y anotar cómo pagaron',
  'setup.feature.sales.gone': 'El cobro y la caja del día',
  'setup.feature.products.what': 'Vender productos en el mostrador y en línea',
  'setup.feature.products.gone': 'Los productos y la tienda',
  'setup.feature.certificates.what': 'Vender y canjear tarjetas de regalo',
  'setup.feature.certificates.gone': 'Vender y canjear tarjetas de regalo',
  'setup.feature.credit.what': 'Los clientes mantienen un saldo con el negocio',
  'setup.feature.credit.gone': 'Los saldos de los clientes (los saldos se conservan)',
  'setup.feature.reporting.what': 'Ingresos, lo que se vende, los mejores clientes',
  'setup.feature.reporting.gone': 'Los informes',
  'setup.feature.billing.what':
    'Cobrar en línea con PayPal — depósitos y pedidos pagados por adelantado',
  'setup.feature.billing.gone': 'Cobrar en línea',
  'setup.feature.booking_site.what': 'Su propio sitio de reservas',
  'setup.feature.booking_site.gone': 'El sitio web de reservas',
  'setup.feature.notifications.what': 'Recordatorios y confirmaciones a los clientes',
  'setup.feature.notifications.gone': 'Los recordatorios y las confirmaciones',
  'setup.feature.partner_api.what':
    'Permite que el asistente de reservas y los socios consulten horas y reserven',
  'setup.feature.partner_api.gone': 'Reservar a través del asistente y los socios',
  'setup.feature.ordering.what': 'Pedidos para llevar y la pantalla de cocina',
  'setup.feature.ordering.gone': 'Los pedidos y la pantalla de cocina',
  'setup.feature.lodging.what': 'Habitaciones, tarifas por noche y estancias',
  'setup.feature.lodging.gone': 'Las habitaciones y las estancias',
  'setup.feature.departures.what': 'Salidas programadas con plazas a la venta',
  'setup.feature.departures.gone': 'Las excursiones y salidas',
  'setup.feature.photos.what': 'Álbumes que los huéspedes abren con un código en el sitio web',
  'setup.feature.photos.gone': 'Los álbumes de fotos',
  'setup.feature.fulfillment.what': 'Artículos hechos por encargo y enviados por un taller',
  'setup.feature.fulfillment.gone': 'Los artículos e impresiones',
  'setup.feature.admin.what': 'Nuestro propio acceso al negocio — nunca se vende',
  'setup.feature.admin.gone': 'El acceso del operador',

  'setup.settings.counter': 'En el mostrador',
  'setup.settings.shop': 'Tienda y recogida',
  'setup.settings.kitchen': 'Cocina y pedidos',
  'setup.settings.website': 'Reservas en el sitio web',
  'setup.settings.tenders': 'Formas de pago que aceptan',
  'setup.settings.tendersHint': 'Los botones al cobrar. Cualquiera se sigue registrando si se usa.',
  'setup.settings.tips': 'Propinas',
  'setup.settings.tipsHint': 'Añade un campo de propina al cobrar.',
  'setup.settings.editors': 'Quién puede cambiar lo que venden',
  'setup.settings.editorsHint':
    'Productos, servicios y sus fotos, y lo que aparece en su sitio web — desde la app del personal. Las cuentas de solo lectura nunca pueden.',
  'setup.settings.editors.owner': 'Solo el propietario',
  'setup.settings.editors.ownerAndStaff': 'El propietario y el personal',
  'setup.settings.on': 'Sí',
  'setup.settings.off': 'No',
  'setup.settings.save': 'Guardar',
  'setup.settings.undo': 'Deshacer los cambios',
  'setup.settings.forFeature': ({ feature }) =>
    `Para cuando ${feature} esté activo — hasta entonces no hace nada.`,
  'setup.settings.none':
    'Todavía no hay nada que ajustar aquí: estas preguntas llegan con Cobro, Tienda, Pedidos de restaurante y Sitio web.',
  'setup.settings.showAll': 'Mostrar los ajustes de funciones desactivadas',
  'setup.settings.hideAll': 'Ocultar los ajustes de funciones desactivadas',
  'setup.settings.confirm.title': ({ section }) => `¿Guardar «${section}»?`,
  'setup.settings.confirm.lead': ({ business }) =>
    `Esto cambia lo que ven el personal y los clientes de ${business}:`,
  'setup.settings.confirm.save': 'Guardar',
  'setup.settings.change': ({ question, answer }) => `**${question}** ${answer}.`,
  'setup.settings.cleared': 'sin valor predeterminado',
  'setup.settings.notSet': ({ list }) =>
    `Sin responder todavía, así que lo que se ve es lo que ocurre de todos modos: ${list}.`,
  'setup.settings.quoted': ({ question }) => `«${question}»`,
  'setup.tender.cash': 'Efectivo',
  'setup.tender.check': 'Cheque',
  'setup.tender.card': 'Tarjeta',
  'setup.tender.other': 'Otro',

  'setup.rule.title': 'Cómo se reserva el día',
  'setup.rule.try': 'Probar un día…',
  'setup.rule.change': 'Cambiar…',
  'setup.rule.tryNote':
    '«Probar un día» abre el simulador: escriba un día real de su agenda y compare lo que ofrecería cada regla.',
  'setup.rule.minutes': ({ n }) => `${String(n)} ${plural(n, 'minuto', 'minutos')}`,
  'setup.rule.hours': ({ n }) => `${String(n)} ${plural(n, 'hora', 'horas')}`,
  'setup.rule.days': ({ n }) => `${String(n)} ${plural(n, 'día', 'días')}`,
  'setup.rule.hoursMinutes': ({ h, m }) => `${String(h)} h ${String(m)} min`,
  'setup.rule.step': ({ n }) => `pasos de ${String(n)} minutos`,
  'setup.rule.mode.open': 'ofrece cualquier hora libre',
  'setup.rule.mode.no_slivers': 'nunca deja un hueco demasiado corto para venderlo',
  'setup.rule.mode.touching': 'solo ofrece horas pegadas a algo ya reservado',
  'setup.rule.sentence': ({ name, step, gap, mode }) =>
    `${name} trabaja en **${step}**, deja **${gap}** entre clientes y **${mode}**.`,
  'setup.rule.sentenceNoGap': ({ name, step, mode }) =>
    `${name} trabaja en **${step}**, no deja tiempo entre clientes y **${mode}**.`,
  'setup.rule.before': ({ gap }) => `Antes de cada cliente también se dejan libres **${gap}**.`,
  'setup.rule.noNotice': 'Sin antelación mínima.',
  'setup.rule.notice': ({ notice }) =>
    `Los clientes reservan con al menos **${notice}** de antelación.`,
  'setup.rule.cutoff': ({ time }) => `Después de las **${time}**, el mismo día ya no se ofrece.`,
  'setup.rule.keptBack': ({ count }) =>
    `**${String(count)} ${plural(count, 'franja', 'franjas')} de horas** ${plural(count, 'queda reservada', 'quedan reservadas')} fuera de las reservas en línea.`,
  'setup.rule.packing': 'Primero se ofrecen las horas que mejor llenan el día.',
  'setup.rule.fineTuned': 'Hay más ajustes finos; «Probar un día…» los muestra todos.',
  'setup.rule.editTitle': 'Cambiar cómo se reserva el día',
  'setup.rule.modeLegend': 'Qué horas se ofrecen',
  'setup.rule.gapAfter': 'Tiempo entre clientes (minutos)',
  'setup.rule.gapBefore': 'Tiempo libre antes de cada cliente (minutos)',
  'setup.rule.noticeLabel': 'Antelación mínima (minutos)',
  'setup.rule.noticeHint':
    'Con cuánta antelación debe reservar un cliente. 0 es sin mínimo; 1440 es un día.',
  'setup.rule.cutoffLabel': 'Hora límite del mismo día',
  'setup.rule.cutoffHint': 'Después de esta hora, el mismo día ya no se ofrece. Vacío si no hay.',
  'setup.rule.stepNote':
    'El paso se cambia desde la línea de comandos; las reglas finas están en «Probar un día…».',
  'setup.rule.apply': 'Aplicar…',
  'setup.rule.confirm.title': '¿Aplicar esta regla de reservas?',
  'setup.rule.confirm.consequence': ({ sentence }) =>
    `A partir de ahora: ${sentence} Lo que se ofrece a clientes y socios cambia al instante. El personal que reserva a mano nunca queda limitado por esto.`,
  'setup.rule.confirm.apply': 'Aplicar',
  'setup.rule.applied': 'Guardado — esto es lo que se ofrece a clientes y socios desde ahora.',
  'setup.sim.showConfig': 'Mostrar los ajustes como texto',

  'setup.details.timezone': 'Zona horaria',
  'setup.details.currency': 'Moneda',
  'setup.details.stage': 'Etapa',
  'setup.details.exact': 'Las horas son exactas — la agenda de aquí es la real.',
  'setup.details.approximate':
    'Las horas son aproximadas — la agenda de aquí todavía no es la real, así que los socios confirman antes de prometer.',
  'setup.details.catalog': 'Versión del menú',
  'setup.details.catalogNote': ({ n }) => `${String(n)} — sube cada vez que se guarda el menú`,
  'setup.details.noCatalog': 'Todavía sin menú',

  'setup.close.title': 'Cerrar este negocio',
  'setup.close.lede': ({ name }) =>
    `Dar de baja detiene todas las reservas con ${name} — en la aplicación, en línea y a través de socios — y comienza la exportación y la entrega: todo su historial se exporta, se comprueba y se entrega a la dueña o al dueño. Dar de baja no borra nada.`,
  'setup.close.protected': ({ name }) =>
    `${name} está protegido, así que no se puede borrar nada hasta quitar la protección desde la línea de comandos.`,
  'setup.close.unprotected':
    'Sus datos se pueden borrar a mano 90 días después de la baja, una vez hecha la entrega.',
  'setup.close.button': 'Dar de baja…',
  'setup.close.already': ({ name }) =>
    `${name} está dado de baja: no se puede reservar nada. Para recuperarlo, use «Cambiar de etapa» arriba en la página.`,
  'setup.close.handover':
    'Exporte sus datos y entrégueselos en persona o en un medio que controlen — nunca por correo electrónico. La lista de pasos para dar de baja lo explica.',
  'setup.close.type': ({ id }) => `Escriba ${id} para confirmar`,

  'look.title': 'El aspecto de su aplicación',
  'look.chip.own': 'Aspecto propio',
  'look.chip.standard': 'Aspecto estándar',
  'look.lede': ({ name }) =>
    `Cómo se ve la aplicación del personal de ${name} en sus teléfonos y tabletas: dos colores, los títulos, el nombre arriba, el botón de añadir y la fotografía de fondo.`,
  'look.standard': ({ name }) =>
    `La aplicación de ${name} se ve como viene: los colores estándar, sin fotografía, el nombre en letra normal y botones de añadir con texto.`,
  'look.change': 'Cambiar el aspecto…',
  'look.clear': 'Quitar',
  'look.editTitle': ({ name }) => `La aplicación de ${name}: su aspecto`,
  'look.draftNote': 'Nada cambia en sus dispositivos hasta que guarde.',

  'look.accent.label': 'Color de acento',
  'look.accent.hint':
    'Botones, enlaces y el nombre de arriba. El tono suave de al lado se calcula a partir de este color y se usa detrás de las filas destacadas.',
  'look.ground.label': 'Color de fondo',
  'look.ground.hint': 'El color bajo la fotografía, y toda la página cuando no hay ninguna.',
  'look.wash': 'Tono suave',
  'look.colour.picker': ({ label }) => `Selector de ${label.toLowerCase()}`,
  'look.colour.hex': ({ label }) => `${label} en código hexadecimal`,
  'look.colour.standard': 'Usar el de la aplicación',
  'look.colour.isStandard': 'El color propio de la aplicación',
  'look.colour.invalid': 'Un código hexadecimal como #c19a3f.',

  'look.font.label': 'Títulos',
  'look.font.system': 'La letra propia de la aplicación',
  'look.font.serif': 'Con serifa (Georgia)',
  'look.font.hint':
    'Solo los títulos y el nombre de arriba. Las listas siguen en la letra de la aplicación para leerse a un brazo de distancia.',

  'look.mark.label': 'El nombre de arriba',
  'look.mark.plain': 'Sencillo: el nombre tal como está escrito',
  'look.mark.hearts':
    'Corazones: la primera palabra grande, la segunda entre corazones, el resto debajo',
  'look.mark.hint':
    'Se construye con el nombre del propio negocio; no hay dónde escribir otro. Un nombre de menos de tres palabras se muestra sencillo.',
  'look.mark.short': ({ name, words }) =>
    `«${name}» tiene ${String(words)} ${plural(words, 'palabra', 'palabras')}, así que su aplicación lo mostrará sencillo: el estilo de corazones necesita tres palabras o más.`,

  'look.button.label': 'El botón de añadir',
  'look.button.plus': 'Más: botones con texto, como viene la aplicación',
  'look.button.heart': 'Corazón: un corazón dorado en los botones de añadir',
  'look.button.hint':
    'Cliente sin cita, cliente nuevo, emitir una tarjeta de regalo. Las frases de la aplicación siguen la elección: con el corazón, al personal se le dice que pulse «el botón del corazón».',

  'look.photos.legend': 'Fotografías de fondo',
  'look.photos.hint':
    'Una para el dispositivo en vertical y otra en horizontal. Con una sola, se usa en ambos sentidos. Queda suavemente desenfocada detrás de las listas.',
  'look.photo.portrait': 'Vertical',
  'look.photo.landscape': 'Horizontal',
  'look.photo.none': 'Ninguna: se ve el color de fondo.',
  'look.photo.of': ({ slot }) => `La fotografía ${slot.toLowerCase()}`,
  'look.photo.upload': ({ slot }) => `Subir la fotografía ${slot.toLowerCase()} desde este equipo`,
  'look.photo.remove': ({ slot }) => `Quitar la fotografía ${slot.toLowerCase()}`,
  'look.photo.removeShort': 'Quitar',
  'look.photo.paste': ({ slot }) => `Dirección de la fotografía ${slot.toLowerCase()}`,
  'look.photo.pasteHint': 'O pegue la dirección https de una imagen que ya sea pública.',
  'look.photo.use': 'Usar',
  'look.photo.badAddress': 'Esa no es una dirección https.',
  'look.photo.uploading': 'Subiendo…',
  'look.upload.hint':
    'JPEG, PNG o WebP, hasta 10 MB. Se guarda en seguida, pero solo se usa cuando guarde el aspecto.',
  'look.upload.off':
    'Esta instalación todavía no puede recibir una imagen desde la consola. Pegue la dirección de una que ya sea pública.',
  'look.upload.heic': ({ file }) =>
    `${file} es HEIC y hay que convertirla primero: la mitad de los navegadores del mundo no la muestran. Conviértala a JPEG y elija el JPEG.`,
  'look.upload.notPicture': ({ file }) => `${file} no es una imagen JPEG, PNG ni WebP.`,
  'look.upload.tooBig': ({ file }) =>
    `${file} pesa más de 10 MB. Exporte una copia más pequeña: la pantalla de un teléfono no necesita más.`,

  'look.preview.title': 'Vista previa',
  'look.preview.hold': 'Cómo se sostiene el dispositivo',
  'look.preview.label': 'Boceto de su aplicación',
  'look.preview.online': 'En línea',
  'look.preview.today': 'Hoy',
  'look.preview.sample': 'Cliente de ejemplo',
  'look.preview.empty': 'No hay nada más en la agenda de hoy.',
  'look.preview.heartHint': ' Añada una con el botón del corazón.',
  'look.preview.walkIn': 'Sin cita',
  'look.preview.newClient': 'Cliente nuevo',
  'look.preview.note':
    'Un boceto con filas inventadas. Los colores, los títulos, el nombre, el botón y la fotografía son las elecciones reales.',

  'look.summary.accent': 'Acento',
  'look.summary.ground': 'Fondo',
  'look.summary.headings': 'Títulos',
  'look.summary.mark': 'Nombre de arriba',
  'look.summary.button': 'Botón de añadir',
  'look.summary.photos': 'Fotografías',
  'look.summary.photosBoth': 'Vertical y horizontal',
  'look.summary.photosPortrait': 'Solo vertical, usada en ambos sentidos',
  'look.summary.photosLandscape': 'Solo horizontal, usada en ambos sentidos',
  'look.summary.photosNone': 'Ninguna',
  'look.summary.standardValue': 'Estándar',
  'look.summary.hearts': 'Corazones',
  'look.summary.heartsShort': 'Corazones (se ve sencillo: el nombre tiene menos de tres palabras)',
  'look.summary.plain': 'Sencillo',
  'look.summary.serif': 'Con serifa',
  'look.summary.heart': 'Corazón',
  'look.summary.plus': 'Más',

  'look.save': 'Guardar el aspecto',
  'look.confirm.title': ({ name }) => `¿Cambiar el aspecto de la aplicación de ${name}?`,
  'look.confirm.consequence': ({ name }) =>
    `Cada teléfono y tableta con la aplicación de ${name} muestra el nuevo aspecto la próxima vez que la aplicación se abre o se reconecta. El sitio web no cambia. No se guardan versiones del aspecto: para volver atrás, vuelva a ajustarlo o quítelo.`,
  'look.clearConfirm.title': ({ name }) =>
    `¿Devolver la aplicación de ${name} a su aspecto estándar?`,
  'look.clearConfirm.consequence': ({ name }) =>
    `Los colores, los títulos, el estilo del nombre, el botón del corazón y las dos fotografías vuelven a como viene la aplicación, en cada dispositivo con la aplicación de ${name} la próxima vez que se abra o se reconecte. No se guarda ninguna copia del aspecto actual.`,
  'look.clearConfirm.confirm': 'Quitar el aspecto',
  'look.saved':
    'Aspecto guardado. Cada dispositivo lo muestra la próxima vez que la aplicación se abre o se reconecta.',
  'look.cleared': 'Aspecto quitado. Su aplicación vuelve a su aspecto estándar.',
  'look.movedFromEditor':
    'Este editor es solo el sitio web. El aspecto de la aplicación del personal — sus colores, el nombre, el botón del corazón y las fotografías de fondo — está en la pestaña Configuración, en «El aspecto de su aplicación».',
};
